// imports
import React, { ReactNode } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { styled } from '@mui/material/styles';
import { LIGHT_GRAY } from '../../../constants/colors';

// Title of accordion
const ReusableAccoridonTypography = styled(Typography)(() => ({
  fontWeight: 'bold',
  fontSize: '1.125rem',
})) as typeof Typography;

// Style for the accordion rectangle that contains the title
const ReusableAccordionSummary = styled(AccordionSummary)(() => ({
  boxShadow: 'none',
  borderRadius: 'none',
  backgroundColor: LIGHT_GRAY,
  border: '1px solid #D1D4D5',
}));

// Icon that indicates that the accordion is open
const ReusableAccoridanRemoveIcon = styled(RemoveIcon)(() => ({
  marginLeft: 'auto',
}));

// Icon that indicates that the accordion is closed
const ReusableAccoridanAddIcon = styled(AddIcon)(() => ({
  marginLeft: 'auto',
}));

const StyledAccordion = styled(Accordion)(() => ({
  // Remove the default MUI margin
  '&.MuiAccordion-root': {
    margin: 0,
    // Ensure focus outline is visible
    '&:focus-within': {
      zIndex: 1,
      position: 'relative',
    },
  },
  // Remove the default MUI border
  '&.MuiAccordion-root:before': {
    display: 'none',
  },
  // Move focus outline inward so it doesn't get cut-off
  '& .MuiAccordionSummary-root:focus-visible': {
    outlineOffset: '-3px',
  },
}));

interface ReusableAccordionProps {
  title: string;
  ariaLabel?: string;
  children: ReactNode;
  'data-testid'?: string;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, newExpanded: boolean) => void;
  accordionStyles?: Record<string, unknown>;
}

const ReusableAccordion: React.FC<ReusableAccordionProps> = ({
  title,
  ariaLabel,
  children,
  'data-testid': dataTestId,
  expanded,
  onChange,
  accordionStyles,
}) => {
  // id is created based on the title; must replace spaces with a character (hyphen)
  const titleNoSpaces = title.replace(/\s+/g, '-');

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={onChange}
      data-testid={dataTestId}
      sx={accordionStyles}
      disableGutters
      slotProps={{ heading: { component: 'h2' } }}
    >
      <ReusableAccordionSummary
        aria-controls={`${titleNoSpaces}-content`}
        id={`${titleNoSpaces}-header`}
        data-testid={`${titleNoSpaces}-header`}
        aria-expanded={expanded}
        aria-label={ariaLabel}
      >
        <ReusableAccoridonTypography>{title}</ReusableAccoridonTypography>
        {expanded ? (
          <ReusableAccoridanRemoveIcon />
        ) : (
          <ReusableAccoridanAddIcon />
        )}
      </ReusableAccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </StyledAccordion>
  );
};

export default ReusableAccordion;
